/**CORE LIBRARIES IMPORT */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";

/**COMPONENTS IMPORT */
import NotFound from "./NotFound";
import PartnerLockup from "../Components/PartnerLockup";
import SectionHeading from "../Components/SectionHeading";
import TurnstileWidget from "../Components/TurnstileWidget";

/**DATA / HOOKS / SERVICES IMPORT */
import { formatEventDate, formatPrice } from "../data/events";
import usePageTitle from "../hooks/usePageTitle";
import { PRIVACY_PATH, REFUND_POLICY_PATH, TERMS_PATH } from "../data/legal";
import { useResolvedEvent } from "../hooks/useContent";
import useEventAvailability, { availabilityLabel } from "../hooks/useEventAvailability";
import {
  createBooking,
  bookingErrorMessage,
  randomToken,
  rememberBookingAccess,
  SIMULATED_TEST_CAPACITY,
  turnstileSiteKey,
} from "../services/booking";

const MotionLink = motion.create(Link);
const STEPS = ["Guests", "Your details", "Review"];
const money = (cents, currency) => formatPrice({ amount: cents / 100, currency });

const TestModeBanner = ({ mode }) =>
  mode === "simulation" ? (
    <p className="dev-banner checkout-banner" role="note">
      DEVELOPMENT SIMULATION: no real booking or payment. Runs only in this browser, with a
      simulated test capacity of {SIMULATED_TEST_CAPACITY} places (not a real number).
    </p>
  ) : null;

/**
 * Event checkout: guests → details → review → secure payment.
 * Prices shown here are for display; the server recalculates every amount.
 */
const EventCheckout = () => {
  const { slug } = useParams();
  const { event, loading: eventLoading } = useResolvedEvent(slug);
  usePageTitle(event ? `Book: ${event.title}` : eventLoading ? "Book" : "Event not found");
  const navigate = useNavigate();
  const { loading, availability, mode, enabled } = useEventAvailability(event, { includeSimulation: true });

  const [step, setStep] = useState(0);
  const [guests, setGuests] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  // Turnstile: required whenever the real backend is used. Each token is
  // single-use, so the widget is reset after every attempt.
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [agreed, setAgreed] = useState(false); // always starts unticked
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [error, setError] = useState(null);
  // One attempt = one idempotency key + status token, reused on retries of the
  // same submission and regenerated when the booking details change.
  const attempt = useRef(null);

  const minGuests = availability?.min_guests_per_booking || 1;
  const maxGuests = useMemo(() => {
    if (!availability) return 1;
    const perBooking = availability.max_guests_per_booking || 10;
    return typeof availability.remaining === "number" ? Math.max(Math.min(perBooking, availability.remaining), 1) : perBooking;
  }, [availability]);

  // Start within the event's guest limits once the server's rules are known.
  useEffect(() => {
    setGuests((g) => Math.min(Math.max(g, minGuests), maxGuests));
  }, [minGuests, maxGuests]);

  if (!event && eventLoading) return <main className="simple-page"><p className="section-text" role="status">Loading…</p></main>;
  if (!event) return <NotFound />;

  const unitCents = availability?.price_cents ?? Math.round(event.price.amount * 100);
  const currency = availability?.currency || event.price.currency;
  const totalCents = unitCents * guests;
  const salesOpen = enabled && availability?.on_sale;

  const resetAttempt = () => {
    attempt.current = null;
  };
  const update = (e) => {
    resetAttempt();
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  const changeGuests = (n) => {
    resetAttempt();
    setGuests(Math.min(Math.max(n, minGuests), maxGuests));
  };

  const needsTurnstile = mode === "backend" && Boolean(turnstileSiteKey);

  const submit = async () => {
    if (!agreed) {
      setError("Please confirm that you agree to the Terms & Conditions and the Refund & Cancellation Policy.");
      return;
    }
    if (needsTurnstile && !turnstileToken) {
      setError("Please complete the security check first.");
      return;
    }
    setError(null);
    setSubmitting(true);
    if (!attempt.current) attempt.current = { idempotencyKey: randomToken(), accessToken: randomToken() };
    const token = turnstileToken;
    // The token is consumed by this request whatever the outcome: never reuse it.
    setTurnstileToken(null);
    try {
      const booking = await createBooking(event, { guests, ...form, ...attempt.current, turnstileToken: token, policiesAccepted: agreed });
      rememberBookingAccess(booking.reference, event.slug, attempt.current.accessToken);
      navigate(`/booking/${booking.reference}`);
    } catch (err) {
      setError(bookingErrorMessage(err));
      setSubmitting(false);
      setTurnstileReset((n) => n + 1); // fresh challenge for the retry
    }
  };

  return (
    <main className="checkout-page">
      <section className="page-hero" aria-labelledby="checkout-title">
        <div className="page-hero-inner">
          {event.partners && <PartnerLockup partners={event.partners} label={event.presentedBy} />}
          <p className="page-eyebrow">Book your place</p>
          <h1 id="checkout-title" className="page-title">{event.title}</h1>
          <p className="page-subtitle">
            {formatEventDate(event.date)}
            {event.startTime && <> · {event.startTime}</>}
          </p>
        </div>
      </section>

      <TestModeBanner mode={mode} />

      <div className="checkout-body">
        {!enabled || (!loading && !availability) ? (
          <div className="menu-card checkout-unavailable">
            <SectionHeading title="Online Booking Coming Soon" />
            <p className="section-text">
              Online booking for this event is not open yet. You can still send us an enquiry and we
              will reply by email.
            </p>
            <div className="event-actions">
              <MotionLink to={`/book?event=${event.slug}`} className="hero-btn hero-btn-active" whileHover={{ y: -6, scale: 1.05 }}>
                Send an Enquiry
              </MotionLink>
            </div>
          </div>
        ) : loading ? (
          <p className="section-text checkout-loading" role="status">Checking availability…</p>
        ) : !salesOpen ? (
          <div className="menu-card checkout-unavailable">
            <SectionHeading title={availabilityLabel(availability) || "Booking unavailable"} />
            <p className="section-text">Online booking is not available for this event right now.</p>
            <div className="event-actions">
              <MotionLink to={`/book?event=${event.slug}`} className="hero-btn" whileHover={{ y: -6, scale: 1.05 }}>
                Send an Enquiry
              </MotionLink>
            </div>
          </div>
        ) : (
          <div className="checkout-grid">
            <div className="menu-card checkout-panel">
              <ol className="checkout-steps" aria-label="Booking steps">
                {STEPS.map((label, i) => (
                  <li key={label} className={i === step ? "is-current" : i < step ? "is-done" : undefined} aria-current={i === step ? "step" : undefined}>
                    <span className="checkout-step-num">{i + 1}</span> {label}
                  </li>
                ))}
              </ol>

              {step === 0 && (
                <div className="checkout-step">
                  <h2 className="menu-course">Number of guests</h2>
                  <div className="guest-stepper" role="group" aria-label="Number of guests">
                    <button type="button" className="hero-btn guest-btn" onClick={() => changeGuests(guests - 1)} disabled={guests <= minGuests} aria-label="Remove a guest">−</button>
                    <output className="guest-count" aria-live="polite">{guests}</output>
                    <button type="button" className="hero-btn guest-btn" onClick={() => changeGuests(guests + 1)} disabled={guests >= maxGuests} aria-label="Add a guest">+</button>
                  </div>
                  <p className="section-text">
                    {money(unitCents, currency)} per person · food menu only
                    {typeof availability.remaining === "number" && (
                      <> · {availabilityLabel(availability)}{availability.simulated ? " (simulated)" : ""}</>
                    )}
                  </p>
                  <div className="event-actions">
                    <button type="button" className="hero-btn hero-btn-active" onClick={() => setStep(1)}>Continue</button>
                  </div>
                </div>
              )}

              {step === 1 && (
                <form className="enquiry-form checkout-step" onSubmit={(e) => { e.preventDefault(); setStep(2); }}>
                  <div className="form-field form-field-full">
                    <label htmlFor="co-name">Full name <span aria-hidden="true">*</span></label>
                    <input id="co-name" name="name" type="text" autoComplete="name" required maxLength={120} value={form.name} onChange={update} />
                  </div>
                  <div className="form-field">
                    <label htmlFor="co-email">Email <span aria-hidden="true">*</span></label>
                    <input id="co-email" name="email" type="email" autoComplete="email" required maxLength={254} value={form.email} onChange={update} />
                  </div>
                  <div className="form-field">
                    <label htmlFor="co-phone">Phone <span className="form-optional">(optional)</span></label>
                    <input id="co-phone" name="phone" type="tel" autoComplete="tel" maxLength={40} value={form.phone} onChange={update} />
                  </div>
                  <p className="form-hint form-field-full">* Required. Used only for this booking (confirmation and updates), as explained in our{" "}
                    <Link to={PRIVACY_PATH} target="_blank" rel="noopener">Privacy Policy</Link>.</p>
                  <div className="event-actions form-field-full">
                    <button type="button" className="hero-btn" onClick={() => setStep(0)}>Back</button>
                    <button type="submit" className="hero-btn hero-btn-active">Review Booking</button>
                  </div>
                </form>
              )}

              {step === 2 && (
                <div className="checkout-step">
                  <dl className="details-list">
                    <div className="details-row"><dt>Event</dt><dd>{event.title}</dd></div>
                    <div className="details-row"><dt>Date</dt><dd>{formatEventDate(event.date)}{event.startTime && `, ${event.startTime}`}</dd></div>
                    <div className="details-row"><dt>Guests</dt><dd>{guests}</dd></div>
                    <div className="details-row"><dt>Name</dt><dd>{form.name}</dd></div>
                    <div className="details-row"><dt>Email</dt><dd>{form.email}</dd></div>
                    {form.phone && <div className="details-row"><dt>Phone</dt><dd>{form.phone}</dd></div>}
                  </dl>
                  <p className="checkout-total-line">
                    Total to pay: <strong>{money(totalCents, currency)}</strong>
                    <span className="details-note"> ({guests} × {money(unitCents, currency)})</span>
                  </p>
                  <div className="policy-agree">
                    <input id="co-agree" type="checkbox" checked={agreed} required aria-required="true"
                      onChange={(e) => { setAgreed(e.target.checked); if (e.target.checked) setError(null); }} />
                    <label htmlFor="co-agree">
                      I agree to the{" "}
                      <Link to={TERMS_PATH} target="_blank" rel="noopener">Terms &amp; Conditions</Link>{" "}
                      and acknowledge the{" "}
                      <Link to={REFUND_POLICY_PATH} target="_blank" rel="noopener">Refund &amp; Cancellation Policy</Link>:
                      tickets are non-refundable if I cancel, change my mind or cannot attend. My statutory rights,
                      including a refund if the event is cancelled, are not affected.
                      <span className="sr-only"> (policies open in a new tab)</span>
                    </label>
                  </div>
                  {error && <p className="checkout-error" role="alert">{error}</p>}
                  {needsTurnstile && (
                    <TurnstileWidget siteKey={turnstileSiteKey} resetKey={turnstileReset} onToken={setTurnstileToken} />
                  )}
                  <div className="event-actions">
                    <button type="button" className="hero-btn" onClick={() => setStep(1)} disabled={submitting}>Back</button>
                    <button type="button" className="hero-btn hero-btn-active" onClick={submit} disabled={submitting || !agreed || (needsTurnstile && !turnstileToken)}>
                      {submitting ? "Reserving your places…" : "Proceed to Secure Payment"}
                    </button>
                  </div>
                  <p className="details-note">
                    Your places are held for {availability.hold_minutes || 15} minutes while you pay. Your
                    booking is confirmed only after the payment has been verified.
                  </p>
                </div>
              )}
            </div>

            <aside className="event-details-side checkout-summary" aria-label="Order summary">
              <h2 className="menu-course">Summary</h2>
              <dl className="details-list">
                <div className="details-row"><dt>Food menu</dt><dd>{money(unitCents, currency)} × {guests}</dd></div>
                <div className="details-row checkout-total"><dt>Total</dt><dd>{money(totalCents, currency)}</dd></div>
              </dl>
              {event.drinks && <p className="details-note">{event.drinks.note}</p>}
              <p className="details-note">The final amount is calculated securely on our server.</p>
              <p className="details-note policy-links">
                <Link to={TERMS_PATH} target="_blank" rel="noopener">Terms &amp; Conditions</Link> ·{" "}
                <Link to={REFUND_POLICY_PATH} target="_blank" rel="noopener">Refund &amp; Cancellation Policy</Link> ·{" "}
                <Link to={PRIVACY_PATH} target="_blank" rel="noopener">Privacy Policy</Link>
              </p>
            </aside>
          </div>
        )}
      </div>
    </main>
  );
};

export default EventCheckout;
